/**
 * Shifts Page - Microsoft 365 Style
 * Manage security guard shifts
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Badge,
  Button,
  Input,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { EditRegular, DeleteRegular, SearchRegular, CalendarRegular } from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { TablePagination, SlidePanel } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  notesSection: {
    backgroundColor: '#f3f2f1',
    padding: '16px 20px',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  notesTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  notesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  noteItem: {
    fontSize: '13px',
    color: '#605e5c',
    display: 'flex',
    alignItems: 'flex-start',
  },
  noteLabel: {
    fontWeight: '600',
    color: '#323130',
    minWidth: '140px',
  },
});

interface Shift {
  id: string;
  name: string;
  shift_type?: string;
  start_time?: string;
  end_time?: string;
  clock_count?: number;
  time_tolerance?: number;
  patrol_time_interval?: number;
}

export const ShiftsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    shiftName: '',
    shiftType: 'general',
    startTime: '',
    endTime: '',
    clockCount: '0',
    timeTolerance: '0',
    patrolTimeInterval: '0',
  });

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      console.log('Fetching shifts...');
      const response = await ApiClient.get('/get-shifts');
      console.log('Shifts response:', response.data);
      if (response.data.status === 'success') {
        console.log('Shifts data:', response.data.data);
        setShifts(response.data.data || []);
      } else {
        console.log('Unexpected response status:', response.data);
      }
    } catch (error: unknown) {
      console.error('Fetch shifts error:', error);
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleOpenDialog = (shift?: Shift) => {
    if (shift) {
      setSelectedShift(shift);
      setFormData({
        shiftName: shift.name,
        shiftType: shift.shift_type || 'general',
        startTime: shift.start_time || '',
        endTime: shift.end_time || '',
        clockCount: String(shift.clock_count || 0),
        timeTolerance: String(shift.time_tolerance || 0),
        patrolTimeInterval: String(shift.patrol_time_interval || 0),
      });
    } else {
      setSelectedShift(null);
      setFormData({
        shiftName: '',
        shiftType: 'general',
        startTime: '',
        endTime: '',
        clockCount: '0',
        timeTolerance: '0',
        patrolTimeInterval: '0',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.shiftName.trim()) {
      newToast('warning', 'Please enter shift name');
      return;
    }

    setSubmitting(true);
    console.log('Submitting shift:', formData);

    try {
      const payload = {
        id: selectedShift?.id || '',
        name: formData.shiftName,
        shift_type: formData.shiftType,
        start_time: formData.startTime,
        end_time: formData.endTime,
        clock_count: parseInt(formData.clockCount),
        time_tolerance: parseInt(formData.timeTolerance),
        patrol_time_interval: parseInt(formData.patrolTimeInterval),
      };

      console.log('Payload:', payload);
      const response = await ApiClient.post('/add-update-shift', payload);
      console.log('Response:', response.data);

      if (response.data.status === 'success') {
        newToast('success', 'Shift saved successfully');
        await fetchShifts();
        setDialogOpen(false);
        setFormData({
          shiftName: '',
          shiftType: 'general',
          startTime: '',
          endTime: '',
          clockCount: '0',
          timeTolerance: '0',
          patrolTimeInterval: '0',
        });
      } else {
        console.error('Unexpected response:', response.data);
        newToast('error', response.data?.message || 'Failed to save shift');
      }
    } catch (error: unknown) {
      console.error('Submission error:', error);
      newToast('error', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedShift) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-shift', {
        id: selectedShift.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Shift deleted successfully');
        fetchShifts();
      } else {
        newToast('error', 'Failed to delete shift');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedShift(null);
  };

  const filteredShifts = shifts.filter(shift =>
    shift.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredShifts,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Shifts</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${shifts.length} shifts`}
        </Text>
      </div>

      <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Shift" />

      <div className={styles.searchBar}>
        <Input
          placeholder="Search shifts..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      <ContentCard noPadding>
        <div className={styles.notesSection}>
          <Text className={styles.notesTitle}>Field Definitions:</Text>
          <div className={styles.notesList}>
            <div className={styles.noteItem}>
              <span className={styles.noteLabel}>Clock Count:</span>
              <span>Number of times required to clock in per shift</span>
            </div>
            <div className={styles.noteItem}>
              <span className={styles.noteLabel}>Time Tolerance:</span>
              <span>Allowed lateness in minutes</span>
            </div>
            <div className={styles.noteItem}>
              <span className={styles.noteLabel}>Patrol Interval:</span>
              <span>Interval for patrol checks in minutes</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading shifts...</Text>
          </div>
        ) : filteredShifts.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No shifts found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery
                ? 'Try adjusting your search'
                : 'Click "Add Shift" to create your first shift'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Shift Name</th>
                <th className={styles.th}>Type</th>
                <th className={styles.th}>Start Time</th>
                <th className={styles.th}>End Time</th>
                <th className={styles.th}>Clock Count</th>
                <th className={styles.th}>Time Tolerance</th>
                <th className={styles.th}>Patrol Interval</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(shift => (
                <tr key={shift.id} className={styles.tr}>
                  <td className={styles.td}>
                    <Badge appearance="filled" color="brand">
                      {shift.name}
                    </Badge>
                  </td>
                  <td className={styles.td}>{shift.shift_type || '—'}</td>
                  <td className={styles.td}>{shift.start_time || '—'}</td>
                  <td className={styles.td}>{shift.end_time || '—'}</td>
                  <td className={styles.td}>{shift.clock_count ?? '—'}</td>
                  <td className={styles.td}>{shift.time_tolerance ?? '—'}</td>
                  <td className={styles.td}>{shift.patrol_time_interval ?? '—'}</td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<CalendarRegular />}
                        onClick={() => navigate(`/account/guard-operations/shifts/${shift.id}`)}
                        title="Manage Patrol Windows"
                      >
                        Windows
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EditRegular />}
                        onClick={() => handleOpenDialog(shift)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => {
                          setSelectedShift(shift);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredShifts.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedShift ? 'Edit Shift' : 'Add Shift'}
        subtitle={selectedShift ? 'Update shift details' : 'Create a new shift'}
        width="33%"
        footer={
          <>
            <Button
              appearance="secondary"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave} disabled={submitting}>
              {submitting ? 'Saving...' : selectedShift ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formField}>
          <Field label="Shift Name" required>
            <Input
              value={formData.shiftName}
              onChange={e => setFormData({ ...formData, shiftName: e.target.value })}
              placeholder="Enter shift name"
            />
          </Field>
        </div>

        <div className={styles.formField}>
          <Field label="Shift Type">
            <RadioGroup
              value={formData.shiftType}
              onChange={(e, data) => setFormData({ ...formData, shiftType: data.value })}
            >
              <Radio value="general" label="General" />
              <Radio value="custom" label="Custom" />
            </RadioGroup>
          </Field>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Field label="Start Time">
              <Input
                type="time"
                value={formData.startTime}
                onChange={e => setFormData({ ...formData, startTime: e.target.value })}
              />
            </Field>
          </div>

          <div className={styles.formField}>
            <Field label="End Time">
              <Input
                type="time"
                value={formData.endTime}
                onChange={e => setFormData({ ...formData, endTime: e.target.value })}
              />
            </Field>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Field label="Clock Count">
              <Input
                type="number"
                value={formData.clockCount}
                onChange={e => setFormData({ ...formData, clockCount: e.target.value })}
                placeholder="0"
              />
            </Field>
          </div>

          <div className={styles.formField}>
            <Field label="Time Tolerance (min)">
              <Input
                type="number"
                value={formData.timeTolerance}
                onChange={e => setFormData({ ...formData, timeTolerance: e.target.value })}
                placeholder="0"
              />
            </Field>
          </div>
        </div>

        <div className={styles.formField}>
          <Field label="Patrol Time Interval (min)">
            <Input
              type="number"
              value={formData.patrolTimeInterval}
              onChange={e => setFormData({ ...formData, patrolTimeInterval: e.target.value })}
              placeholder="0"
            />
          </Field>
        </div>
      </SlidePanel>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Shift</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedShift?.name}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ShiftsFluentUI;
